import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type SubscriptionInfo } from '../api';

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const date = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${date} ${time}`;
}

function formatPrice(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('ru-RU')} ₽`;
}

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'дня';
  return 'дней';
}

export function DashboardPage() {
  const [data, setData] = useState<SubscriptionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trialPending, setTrialPending] = useState(false);
  const [trialError, setTrialError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelPending, setCancelPending] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  useEffect(() => {
    api.subscription().then(setData).catch((e) => setError(e instanceof Error ? e.message : 'Ошибка'));
  }, []);

  async function activateTrial() {
    setTrialPending(true);
    setTrialError(null);
    try {
      await api.activateTrial();
      const fresh = await api.subscription();
      setData(fresh);
    } catch (e) {
      setTrialError(e instanceof Error ? e.message : 'Не удалось активировать пробный период');
    } finally {
      setTrialPending(false);
    }
  }

  async function copySubscription(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setToast('Ссылка скопирована');
    } catch {
      setToast('Не удалось скопировать');
    }
    setTimeout(() => setToast(null), 2200);
  }

  async function cancelSubscription() {
    setCancelPending(true);
    setCancelError(null);
    try {
      await api.cancelSubscription();
      const fresh = await api.subscription();
      setData(fresh);
      setCancelConfirm(false);
      setToast('Автопродление отменено');
      setTimeout(() => setToast(null), 2600);
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : 'Не удалось отменить продление');
    } finally {
      setCancelPending(false);
    }
  }

  if (error) return <div className="container"><div className="error">{error}</div></div>;
  if (!data) return <div className="container">Загрузка…</div>;

  const remoteStatus = data.remote?.status ?? null;
  const expireAt = data.remote?.expireAt ?? data.latestGrant?.appliedExpireAt ?? data.latestGrant?.targetExpireAt ?? null;
  const isTrial = data.latestGrant?.source === 'FREE_TRIAL';
  const isActive = remoteStatus === 'ACTIVE';
  const isPaidActive = isActive && !isTrial;
  const statusLabel = isActive
    ? (isTrial ? 'trial' : 'active')
    : (remoteStatus ? remoteStatus.toLowerCase() : 'нет подписки');
  const statusClass = !isActive
    ? (remoteStatus === 'EXPIRED' ? 'err' : 'warn')
    : (isTrial ? 'warn' : 'ok');

  return (
    <div className="container">
      <h1>Подписка</h1>

      {data.trialAvailable && (
        <div className="card">
          <h2>Попробуйте бесплатно</h2>
          <p className="muted">
            {data.trialDurationDays
              ? `Активируйте ${data.trialDurationDays} ${pluralDays(data.trialDurationDays)} бесплатного доступа — без оплаты и привязки карты.`
              : 'Активируйте бесплатный пробный доступ — без оплаты и привязки карты.'}
          </p>
          {trialError && <div className="error" style={{ marginBottom: 12 }}>{trialError}</div>}
          <button type="button" onClick={() => void activateTrial()} disabled={trialPending}>
            {trialPending ? 'Активируем…' : 'Активировать бесплатный день'}
          </button>
        </div>
      )}

      <div className="card">
        <div className="row">
          <span>Статус</span>
          <span className={`badge ${statusClass}`}>{statusLabel}</span>
        </div>
        <div className="row">
          <span>Тариф</span>
          <span>{data.latestGrant?.planTitle ?? '—'}</span>
        </div>
        <div className="row">
          <span>Действует до</span>
          <span>{formatDateTime(expireAt)}</span>
        </div>
        <div className="row">
          <span>Стоимость</span>
          <span>{isTrial ? 'бесплатно' : formatPrice(data.latestGrant?.priceRub)}</span>
        </div>
        <Link
          to="/plans"
          className={`btn btn-block${isPaidActive ? ' secondary' : ''}`}
          style={{ marginTop: 14 }}
        >
          {isActive ? (isPaidActive ? 'Сменить тариф' : 'Продлить подписку') : 'Выбрать тариф'}
        </Link>
      </div>

      {data.subscriptionUrl && (
        <div className="card">
          <h2>Как подключиться</h2>
          <ol className="steps">
            <li><strong>Скачайте Happ</strong> — приложение для вашего устройства (iOS, Android, Windows, macOS).</li>
            <li><strong>Импортируйте подписку</strong> — на странице подключения нажмите «Подключить в Happ» или вставьте ссылку ниже вручную.</li>
            <li><strong>Включите VPN</strong> — выберите сервер в Happ и нажмите подключиться.</li>
          </ol>
          <a className="btn btn-block" href={data.connectUrl ?? data.subscriptionUrl} target="_blank" rel="noopener">
            Открыть страницу подключения
          </a>
          <p className="muted" style={{ marginTop: 16 }}>Ссылка подписки для ручного импорта — нажмите, чтобы скопировать:</p>
          <button
            type="button"
            className="copy-field"
            onClick={() => void copySubscription(data.subscriptionUrl!)}
            title="Скопировать ссылку"
          >
            <code className="copy-field-text">{data.subscriptionUrl}</code>
            <span className="copy-field-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </span>
          </button>
          <p className="muted" style={{ marginTop: 12 }}>
            Это ваш личный доступ. Делитесь ссылкой только с теми, кому доверяете: каждое подключение
            расходует лимит устройств вашей подписки.
          </p>
          <p className="muted" style={{ marginTop: 14 }}>
            Что-то не работает? <a href="https://t.me/orbanvpn_support" target="_blank" rel="noopener">Напишите в поддержку</a>.
          </p>
        </div>
      )}

      {data.hasActiveRecurring && (
        <div className="card">
          <h2>Автопродление</h2>
          <p className="muted">
            Подписка продлевается автоматически. Можно отключить автопродление в любой момент —
            доступ сохранится до конца уже оплаченного периода, повторных списаний не будет.
          </p>
          {cancelError && <div className="error" style={{ marginBottom: 12 }}>{cancelError}</div>}
          {!cancelConfirm ? (
            <button type="button" className="secondary" onClick={() => { setCancelConfirm(true); setCancelError(null); }}>
              Отменить продление
            </button>
          ) : (
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => void cancelSubscription()} disabled={cancelPending}>
                {cancelPending ? 'Отменяем…' : 'Да, отключить автопродление'}
              </button>
              <button type="button" className="secondary" onClick={() => setCancelConfirm(false)} disabled={cancelPending}>
                Назад
              </button>
            </div>
          )}
        </div>
      )}
      {!data.hasTelegramLink && (
        <div className="card">
          <h2>Привяжите Telegram</h2>
          <p className="muted">
            Это необязательно: подписку можно купить и продлить прямо здесь. Но с привязанным Telegram удобнее —
            приходят уведомления о продлении и доступна программа «Пригласи друга».
          </p>
          <a href="https://t.me/orbanvpn_bot?start=link" target="_blank" rel="noopener" className="btn secondary">
            Открыть бот
          </a>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
